import React, { memo } from "react";
import { useDrag, useDrop } from "react-dnd";

import { endDragPreview, useHideDraggedRow } from "utils/drag-preview";

// This is a type used internally to keep track of the item being dragged.
export interface DraggableItemType {
  id: string;
  order: number;
}

/**
 *  Properties for supporting dropping an item on this component's children.
 *  `onDraggedItemHover` The function to call when the item being dragged is moved over this drop zone to update
 *    the local state (typically intended to mimic the final state if dropped here).
 * `dropIndex` The index the item being dragged will be set to if it moves over this drop zone.
 * `dropContainerId` The parent container id the item being dragged will be set to if it moves over this drop zone.
 */
interface DropProps {
  onDraggedItemHover: (
    item: DraggableItemType,
    toIndex: number | undefined,
    dropContainerId?: string | number,
  ) => void;
  dropIndex: number | undefined; // section_user and course_user (used by BreakoutControlPanel and GroupConfigCard) do not need a dropIndex
  dropContainerId?: string | number;
}

/**
 * Properties for supporting dragging an item from this component's children.
 * `itemId` The id of the item being dragged.
 * `onDropItem` The function to call when the item being dragged is dropped on a drop zone.
 */
interface DragProps {
  item: DraggableItemType;
  // `false` restores the drag preview, the same as dropping off a list. Anything else commits it.
  onDropItem?: (
    droppedItem: DraggableItemType,
    dropIndex: number | undefined,
    sourceContainerId?: string | number,
  ) => void | boolean | Promise<void | boolean>;
}

// TODO: Add enforced typescript for supporting either Drag or Drop only,
//  note this requires adding some more guards in the code.
export interface DragAndDropProps extends Partial<DropProps>, Partial<DragProps> {
  dragItemKind:
    | "topic"
    | "activity"
    | "page"
    | "term"
    | "course"
    | "section"
    | "section_user"
    | "class_user";
  draggable?: boolean;
  droppable?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * For supporting Drag or Drop functionality for its children. To use this component
 * for draggable or droppable, the `draggable` or `droppable` props must be set to true
 * and the associated DragProps or DropProps must be set, respectively.
 * The dragging and dropping will only be triggered for the `dragItemKind` specified.
 * Use multiple DragAndDrop components for supporting multiple dragItemKinds.
 */
export const DragAndDrop = memo(function DragAndDropInternal({
  children,
  dragItemKind,
  draggable = false,
  droppable = false,
  className,
  dropIndex,
  item,
  onDraggedItemHover,
  onDropItem,
  dropContainerId,
}: DragAndDropProps) {
  // We cannot make a hook conditionally created in React, but the useDrag and useDrop hooks code will only be used if
  // the component is `draggable` or `droppable` and has implemented the associated props.
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: dragItemKind,
      item,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: () => Boolean(onDropItem && draggable),
      end: (droppedItem, monitor) => {
        if (!monitor.didDrop()) {
          endDragPreview(false);
          return;
        }
        if (!onDropItem) {
          endDragPreview(true);
          return;
        }

        const accepted = onDropItem(droppedItem, dropIndex, dropContainerId);
        // A drop can wait on a confirm dialog. Leave the preview in place until that resolves.
        if (accepted instanceof Promise) {
          accepted.then(
            (value) => endDragPreview(value !== false),
            () => endDragPreview(false),
          );

          return;
        }

        endDragPreview(accepted !== false);
      },
    }),
    [draggable, item, onDraggedItemHover, onDropItem, dragItemKind],
  );

  const [, drop] = useDrop(
    () => ({
      accept: dragItemKind,
      canDrop: () => !!(onDraggedItemHover && droppable),
      hover(draggedItem: DraggableItemType) {
        if (onDraggedItemHover) {
          // Hovering the dragged page itself still has to announce this container, or the
          // activity we left keeps its preview. A parent container drop zone (no item) calls
          // this repeatedly while the pointer stays over it.
          onDraggedItemHover(draggedItem, dropIndex, dropContainerId);
        }
      },
    }),
    [onDraggedItemHover, item, dragItemKind, dropIndex, dropContainerId],
  );

  const hideRow = useHideDraggedRow(item?.id, dropContainerId);

  // When we hide it, we just shrink it to invisible.  It's still in the DOM
  const rowStyle = hideRow
    ? {
        opacity: 0,
        height: 0,
        minHeight: 0,
        overflow: "hidden",
        margin: 0,
        padding: 0,
        border: "none",
      }
    : { opacity: isDragging ? 0 : 1 };

  return (
    <div
      className={className}
      ref={(node) => {
        drag(drop(node));
      }}
      style={rowStyle}
    >
      {children}
    </div>
  );
});
